
import Vue, { PropType } from 'vue';
import Api from '@/core/api';
import Icon from '@/common/icon/Icon.vue';
import { PlayerState } from '@/core/player-state';
import { isFunction, get } from 'lodash';
import { Unsubscriber } from '@/core/event-bus';

export default Vue.extend({
    name: 'BigUnmuteButton',

    components: {
        Icon
    },

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        labels: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        }
    },

    data() {
        return {
            muted: true,
            isPlaying: false,
            videoLoaded: false,
            videoElapsedSecs: 0,
            unsubscribers: [] as Unsubscriber[],
            hasBeenUnmuted: false
        };
    },

    computed: {
        showUnmuteButton(): boolean {
            if (this.hasBeenUnmuted) {
                return false;
            }

            // The video is not playing, we hide the unmute button (the play button is more appropriate)
            if (!this.isPlaying || !this.videoLoaded || !this.videoElapsedSecs) {
                return false;
            }

            return this.muted;
        }
    },

    mounted() {
        const { unsubscriber, state } = this.api.onStateChange(
            (partial: Partial<PlayerState>) => {
                this.onNewState(partial);
            }
        );

        this.onNewState(state);

        this.unsubscribers.push(
            this.api.onCustomAction((payload, context) => {
                if (context === 'autoplayed') {
                    // Each time the video is autoplayed, we reset the state, this can happen on initial start,
                    // if source changes, or if the browser decides to force pause, and we try to play again muted
                    this.hasBeenUnmuted = !get(payload, 'muted');
                }
            }),
            unsubscriber
        );
    },

    beforeDestroy() {
        for (const unsubscriber of this.unsubscribers) {
            if (isFunction(unsubscriber)) {
                unsubscriber();
            }
        }
    },

    methods: {
        onNewState(newState:Partial<PlayerState>) {
            this.videoLoaded = get(newState, 'videoLoaded', this.videoLoaded);
            this.isPlaying = get(newState, 'isPlaying', this.isPlaying);
            this.muted = get(newState, 'muted', this.muted);
            this.videoElapsedSecs = get(newState, 'videoElapsedSecs', this.videoElapsedSecs);
        },

        click() {
            this.api.unmute();
            this.hasBeenUnmuted = true;
        }
    }
});
